import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { CheckCircle, Copy } from 'lucide-react'

export const Route = createFileRoute('/docs/getting-started')({ 
  component: GettingStarted
})

function GettingStarted() {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Getting Started</h1>
          <p className="text-xl text-gray-600">
            Follow this guide to create your first API key and start making requests in minutes.
          </p>
        </div>

        <div className="space-y-8">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="outline">Step 1</Badge>
                <CardTitle>Create Your Account</CardTitle>
              </div>
              <CardDescription>
                Sign up with GitHub to get started with our platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span>Sign in with your GitHub account</span>
              </div>
              <p className="text-gray-600 mt-2">
                We use GitHub OAuth for secure authentication. Your GitHub account will be linked to your PlusE Finance dashboard.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="outline">Step 2</Badge>
                <CardTitle>Choose Your Plan</CardTitle>
              </div>
              <CardDescription>
                Select a subscription plan or use pay-as-you-go billing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium">Free Tier</h3>
                  <p className="text-sm text-gray-600">1,000 requests/month</p>
                  <p className="text-2xl font-bold">$0</p>
                </div>
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium">Pro Plan</h3>
                  <p className="text-sm text-gray-600">100,000 requests/month</p>
                  <p className="text-2xl font-bold">$19<span className="text-sm font-normal">/month</span></p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="outline">Step 3</Badge>
                <CardTitle>Generate Your API Key</CardTitle>
              </div>
              <CardDescription>
                Create your first API key from the dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Navigate to the Dashboard → API Keys section</li>
                <li>Click "Create New API Key"</li>
                <li>Give your key a descriptive name</li>
                <li>Copy and securely store your API key</li>
              </ol>
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Important:</strong> Your API key will only be shown once. Make sure to copy and store it securely.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="outline">Step 4</Badge>
                <CardTitle>Make Your First Request</CardTitle>
              </div>
              <CardDescription>
                Test your API key with a simple request
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">cURL Example</h4>
                  <div className="bg-gray-900 rounded-lg p-4 relative">
                    <pre className="text-green-400 text-sm overflow-x-auto">
{`curl -X GET "https://api.example.com/v1/test" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`}
                    </pre>
                    <Button
                      size="sm"
                      variant="outline"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(`curl -X GET "https://api.example.com/v1/test" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">JavaScript Example</h4>
                  <div className="bg-gray-900 rounded-lg p-4 relative">
                    <pre className="text-green-400 text-sm overflow-x-auto">
{`const response = await fetch('https://api.example.com/v1/test', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

const data = await response.json();
console.log(data);`}
                    </pre>
                    <Button
                      size="sm"
                      variant="outline"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(`const response = await fetch('https://api.example.com/v1/test', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

const data = await response.json();
console.log(data);`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Next Steps</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <a href="/docs" className="block p-4 border rounded-lg hover:bg-gray-50">
                  <h3 className="font-medium text-gray-900">API Reference</h3>
                  <p className="text-sm text-gray-600">Explore all available endpoints and parameters</p>
                </a>
                <a href="/dashboard" className="block p-4 border rounded-lg hover:bg-gray-50">
                  <h3 className="font-medium text-gray-900">Dashboard</h3>
                  <p className="text-sm text-gray-600">Monitor your usage and manage your API keys</p>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  )
}